import { useStatuses } from '@/features/backlog/statuses';

/**
 * The status swatch used across every view (table, kanban, filters, drawer): a colour-filled circle
 * with a small contrasting glyph inside that hints at the workflow stage, so a status reads at a
 * glance. Statuses are per-board custom data with no stored icon, so the glyph is DERIVED from
 * keywords in the label. Only five stages get a mark — everything else is a plain colour circle:
 *   not started → ✕   ·   in progress → clock   ·   to test → test tube   ·   complete → ✓   ·   dropped → ⁄
 * The glyphs share one line style (currentColor strokes, 2.2 weight, round caps) so they read as a set.
 */
export type StatusKind = 'todo' | 'progress' | 'test' | 'done' | 'dropped' | 'none';

/**
 * Derive the workflow kind purely from the label; unknown labels get no glyph. done and dropped are
 * the backend's DoneStatusRe / DroppedStatusRe (backend/pkg/defs/reports.go) — keep them in sync, so
 * the web counts what search `state:open` and Reports count. dropped = closed WITHOUT being done
 * (Dropped, Won't fix, Archived, Cancelled, Duplicate…); a label matching both is done.
 */
export const statusKind = (label: string): StatusKind => {
  const l = label.toLowerCase();
  if (/(complete|done|closed|shipped|resolved|finished|merged|deployed|live)/.test(l)) return 'done';
  if (/(drop|won.?t.?(fix|do)|will.?not.?(fix|do)|not.?fixing|archiv|cancel|reject|obsolete|duplicate|discard)/.test(l)) return 'dropped';
  if (/(to.?test|testing|\btest\b|\bqa\b|\buat\b|verify)/.test(l)) return 'test';
  if (/(in.?progress|progress|doing|\bwip\b|active|working|develop|building)/.test(l)) return 'progress';
  if (/(not.?started|to.?do|\btodo\b|backlog|\bnew\b|\bopen\b|ready|triage|icebox)/.test(l)) return 'todo';
  return 'none';
};

/** Closed = done or dropped: no work left on it (the items search `state:open` leaves out). Progress
 *  bars count closed items as finished, so an epic whose leftovers were dropped still reaches 100%. */
export const isClosedKind = (kind: StatusKind): boolean => kind === 'done' || kind === 'dropped';

/** A progress count in words: "6/10 done", or "8/10 closed · 2 dropped" once some are dropped, so
 *  "done" never counts a dropped item. */
export const closedLabel = (closed: number, dropped: number, total: number): string =>
  dropped > 0 ? `${closed}/${total} closed · ${dropped} dropped` : `${closed}/${total} done`;

/** Pick a black/white glyph colour that reads on the given fill (perceived luminance threshold). */
const readableOn = (hex: string): string => {
  const h = (hex || '').replace('#', '');
  const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (n.length < 6) return 'rgba(255,255,255,0.95)';
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.62 ? 'rgba(0,0,0,0.78)' : 'rgba(255,255,255,0.97)';
};

/** The inner glyph for a kind, drawn in currentColor, centred in the 24×24 box. Harmonised weight
 *  (round caps/joins); the caller sizes it to fill most of the circle. */
function KindGlyph({ kind, px }: { kind: StatusKind; px: number }) {
  if (kind === 'none') return null;
  const svg = {
    width: px,
    height: px,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2.2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true as const,
  };
  if (kind === 'done') // ✓
    return (
      <svg {...svg}>
        <path d="M5.5 12.5l4 4L18.5 7" />
      </svg>
    );
  if (kind === 'dropped') // dropped / won't fix — a single slash, "struck off"
    return (
      <svg {...svg}>
        <path d="M16.5 7.5l-9 9" />
      </svg>
    );
  if (kind === 'todo') // not started — a clean ✕
    return (
      <svg {...svg}>
        <path d="M7 7l10 10M17 7L7 17" />
      </svg>
    );
  if (kind === 'progress') // clock — hands radiating from the disc's centre
    return (
      <svg {...svg}>
        <path d="M12 6.6V12l3.6 2.1" />
      </svg>
    );
  // to test — a conical (Erlenmeyer) flask/beaker with a liquid line
  return (
    <svg {...svg} strokeWidth={2}>
      <path d="M9.5 4.5h5" />
      <path d="M10.5 4.5v5L7 17c-.6 1.3.3 2.6 1.7 2.6h6.6c1.4 0 2.3-1.3 1.7-2.6L13.5 9.5v-5" />
      <path d="M8.6 14h6.8" />
    </svg>
  );
}

export default function StatusDot({ value, size = 18 }: { value: string; size?: number }) {
  const { metaOf } = useStatuses();
  const meta = metaOf(value);
  const kind = statusKind(meta.label);
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full ring-1 ring-inset ring-black/15"
      style={{ width: size, height: size, background: meta.color, color: readableOn(meta.color) }}
      title={meta.label}
    >
      <KindGlyph kind={kind} px={Math.round(size * 0.86)} />
    </span>
  );
}
